import { Plus, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { getRules, getRule, createRule, updateRule, deleteRule } from '../../../api/point.api';
import styles from '../../../assets/scss/pages/PointRuleList.module.scss';
import KeypadInput from '../../../components/common/KeypadInput';

function formatVND(val) {
    const num = Math.round(Number(val) || 0);
    return num.toLocaleString('vi-VN');
}

// Ô nhập tiền VND: bấm vào bung bàn phím số ảo. Chỉ số nguyên (không thập phân),
// onChange trả về chuỗi chữ số thô như trước.
function VNDInput({ className, value, onChange, placeholder, title }) {
    return (
        <KeypadInput
            className={className}
            mode="integer"
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            title={title || 'Nhập tiền'}
            suffix="đ"
        />
    );
}

function getRuleFormula(rule) {
    if (rule.type === 'redeem') return `1 điểm = ${formatVND(rule.rewardValue)} VND`;
    if (rule.earnMode === 'by_bill') return `Mỗi hoá đơn = ${Math.round(Number(rule.rewardValue) || 0)} điểm`;
    return `Chi ${formatVND(rule.pointsRequired)} VND = ${Math.round(Number(rule.rewardValue) || 0)} điểm`;
}

export default function PointRuleList() {
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toastMsg, setToastMsg] = useState(null);

    // Panel state
    const [showPanel, setShowPanel] = useState(false);
    const [panelMode, setPanelMode] = useState('create');
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState({
        type: 'earn', earnMode: 'by_amount', pointsRequired: '', rewardValue: '',
        minOrderValue: '', description: '', isActive: true,
    });
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);

    const showToast = useCallback((type, text, duration = 1500) => {
        setToastMsg({ type, text });
        setTimeout(() => setToastMsg(null), duration);
    }, []);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await getRules();
            setRules(Array.isArray(data) ? data : (data?.rules || []));
        } catch { setRules([]); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchList(); }, [fetchList]);

    const resetForm = () => ({
        type: 'earn', earnMode: 'by_amount', pointsRequired: '', rewardValue: '',
        minOrderValue: '', description: '', isActive: true,
    });

    const openCreate = () => {
        setPanelMode('create');
        setEditId(null);
        setForm(resetForm());
        setFormError('');
        setShowPanel(true);
    };

    const openEdit = async (id) => {
        setPanelMode('edit');
        setEditId(id);
        setFormError('');
        try {
            const { data } = await getRule(id);
            const r = data.rule || data;
            setForm({
                type: r.type || 'earn',
                earnMode: r.earnMode || 'by_amount',
                pointsRequired: String(Math.round(Number(r.pointsRequired) || 0)),
                rewardValue: String(Math.round(Number(r.rewardValue) || 0)),
                minOrderValue: r.minOrderValue ? String(Math.round(Number(r.minOrderValue))) : '',
                description: r.description || '',
                isActive: r.isActive !== false,
            });
        } catch {
            const r = rules.find((x) => x.id === id);
            if (r) setForm({
                type: r.type, earnMode: r.earnMode || 'by_amount',
                pointsRequired: String(r.pointsRequired || ''), rewardValue: String(r.rewardValue || ''),
                minOrderValue: r.minOrderValue ? String(r.minOrderValue) : '', description: r.description || '',
                isActive: r.isActive !== false,
            });
        }
        setShowPanel(true);
    };

    const closePanel = () => { setShowPanel(false); setFormError(''); setEditId(null); };

    const validate = () => {
        if (!form.rewardValue) return 'Vui lòng nhập giá trị điểm';
        if ((form.type === 'earn' && form.earnMode === 'by_amount') || form.type === 'redeem') {
            if (!form.pointsRequired) return form.type === 'earn' ? 'Vui lòng nhập số tiền cần chi' : 'Vui lòng nhập số điểm quy đổi';
        }
        return '';
    };

    const handleSave = async () => {
        const err = validate();
        if (err) { setFormError(err); return; }
        setSaving(true);
        setFormError('');
        try {
            const desc = form.type === 'redeem'
                ? `${form.pointsRequired || 0} điểm = ${formatVND(form.rewardValue || 0)} VNĐ`
                : form.earnMode === 'by_bill'
                    ? `Mỗi hoá đơn = ${form.rewardValue || 0} điểm`
                    : `Chi ${formatVND(form.pointsRequired || 0)} VNĐ = ${form.rewardValue || 0} điểm`;

            if (panelMode === 'create') {
                await createRule({
                    type: form.type,
                    earnMode: form.type === 'redeem' ? 'by_amount' : form.earnMode,
                    pointsRequired: Number(form.pointsRequired) || 0,
                    rewardValue: Number(form.rewardValue) || 0,
                    minOrderValue: form.minOrderValue ? Number(form.minOrderValue) : null,
                    description: desc,
                });
                showToast('success', 'Tạo quy tắc thành công');
            } else {
                await updateRule(editId, {
                    earnMode: form.type === 'redeem' ? 'by_amount' : form.earnMode,
                    pointsRequired: Number(form.pointsRequired) || 0,
                    rewardValue: Number(form.rewardValue) || 0,
                    minOrderValue: form.minOrderValue ? Number(form.minOrderValue) : null,
                    description: desc,
                    isActive: form.isActive,
                });
                showToast('success', 'Cập nhật thành công');
            }
            closePanel();
            fetchList();
        } catch (e) {
            setFormError(e.response?.data?.message || 'Thao tác thất bại');
        } finally { setSaving(false); }
    };

    const handleDelete = async () => {
        if (!editId) return;
        if (!window.confirm('Bạn có chắc muốn xoá quy tắc này?')) return;
        try {
            await deleteRule(editId);
            showToast('success', 'Xoá thành công');
            closePanel();
            fetchList();
        } catch { showToast('error', 'Xoá thất bại', 2500); }
    };

    const showPointsRequired = (form.type === 'earn' && form.earnMode === 'by_amount') || form.type === 'redeem';

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h1 className={styles.title}>Quy tắc điểm</h1>
                    <p className={styles.subtitle}>{rules.length} quy tắc</p>
                </div>
                <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={openCreate}>
                    <Plus /> Tạo quy tắc
                </button>
            </div>

            <div className={styles.layout}>
                {/* LEFT: Grid */}
                <div className={styles.layoutLeft}>
                    {loading ? (
                        <div className={styles.loading}><div className={styles.spinner} /> Đang tải...</div>
                    ) : rules.length === 0 ? (
                        <div className={styles.empty}>Chưa có quy tắc điểm nào</div>
                    ) : (
                        <div className={styles.grid}>
                            {rules.map((r) => (
                                <div
                                    key={r.id}
                                    className={`${styles.card}${editId === r.id ? ` ${styles.cardActive}` : ''}`}
                                    onClick={() => openEdit(r.id)}
                                >
                                    <div className={styles.cardBadges}>
                                        <span className={`${styles.badge} ${r.type === 'earn' ? styles.badgeEarn : styles.badgeRedeem}`}>
                                            {r.type === 'earn' ? 'Nhận điểm' : 'Đổi điểm'}
                                        </span>
                                        <span className={`${styles.badge} ${r.isActive !== false ? styles.badgeActive : styles.badgeInactive}`}>
                                            {r.isActive !== false ? 'Đang bật' : 'Tắt'}
                                        </span>
                                    </div>
                                    <div className={styles.cardFormula}>{getRuleFormula(r)}</div>
                                    {r.minOrderValue > 0 && (
                                        <div className={styles.cardMeta}>Đơn tối thiểu: {formatVND(r.minOrderValue)} VND</div>
                                    )}
                                    {r.description && <div className={styles.cardDesc}>{r.description}</div>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* RIGHT: Sidebar Panel */}
                {showPanel && (
                    <div className={styles.layoutRight}>
                        <div className={styles.panel}>
                            <div className={styles.panelHeader}>
                                <h3 className={styles.panelTitle}>
                                    {panelMode === 'create' ? 'Tạo quy tắc' : 'Chỉnh sửa'}
                                </h3>
                                <button className={styles.panelClose} onClick={closePanel}><X size={16} /></button>
                            </div>

                            <div className={styles.panelBody}>
                                {formError && <div className={styles.formError}>{formError}</div>}

                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Loại <span>*</span></label>
                                    <div className={styles.typeToggle}>
                                        <button className={`${styles.typeBtn}${form.type === 'earn' ? ` ${styles.typeBtnActive}` : ''}`}
                                            onClick={() => setForm((p) => ({ ...p, type: 'earn' }))} disabled={panelMode === 'edit'}>Nhận điểm</button>
                                        <button className={`${styles.typeBtn}${form.type === 'redeem' ? ` ${styles.typeBtnActive}` : ''}`}
                                            onClick={() => setForm((p) => ({ ...p, type: 'redeem' }))} disabled={panelMode === 'edit'}>Đổi điểm</button>
                                    </div>
                                </div>

                                {form.type === 'earn' && (
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Cách tính</label>
                                        <div className={styles.typeToggle}>
                                            <button className={`${styles.typeBtn}${form.earnMode === 'by_amount' ? ` ${styles.typeBtnActive}` : ''}`}
                                                onClick={() => setForm((p) => ({ ...p, earnMode: 'by_amount' }))}>Theo tiền</button>
                                            <button className={`${styles.typeBtn}${form.earnMode === 'by_bill' ? ` ${styles.typeBtnActive}` : ''}`}
                                                onClick={() => setForm((p) => ({ ...p, earnMode: 'by_bill' }))}>Theo hoá đơn</button>
                                        </div>
                                    </div>
                                )}

                                {showPointsRequired && (
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>
                                            {form.type === 'earn' ? 'Số tiền cần chi (VND)' : 'Số điểm quy đổi'} <span>*</span>
                                        </label>
                                        {form.type === 'earn' ? (
                                        <VNDInput className={styles.formInput} value={form.pointsRequired}
                                            onChange={(v) => setForm((p) => ({ ...p, pointsRequired: v }))} placeholder="VD: 10,000"
                                            title="Số tiền cần chi" />
                                    ) : (
                                        <KeypadInput className={styles.formInput} mode="integer" value={form.pointsRequired}
                                            onChange={(raw) => setForm((p) => ({ ...p, pointsRequired: raw }))}
                                            placeholder="VD: 1" title="Số điểm quy đổi" suffix="điểm" />
                                    )}
                                    </div>
                                )}

                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>
                                        {form.type === 'redeem' ? 'Giá trị mỗi điểm (VND)'
                                            : form.earnMode === 'by_bill' ? 'Số điểm nhận / hoá đơn'
                                            : 'Số điểm nhận được'} <span>*</span>
                                    </label>
                                    {form.type === 'redeem' ? (
                                        <VNDInput className={styles.formInput} value={form.rewardValue}
                                            onChange={(v) => setForm((p) => ({ ...p, rewardValue: v }))} placeholder="VD: 200"
                                            title="Giá trị mỗi điểm" />
                                    ) : (
                                        <KeypadInput className={styles.formInput} mode="integer" value={form.rewardValue}
                                            onChange={(raw) => setForm((p) => ({ ...p, rewardValue: raw }))}
                                            placeholder="VD: 1" title="Số điểm nhận" suffix="điểm" />
                                    )}
                                </div>

                                {form.type === 'earn' && (
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Đơn tối thiểu (VND, tuỳ chọn)</label>
                                        <VNDInput className={styles.formInput} value={form.minOrderValue}
                                            onChange={(v) => setForm((p) => ({ ...p, minOrderValue: v }))} placeholder="Để trống nếu không yêu cầu"
                                            title="Đơn tối thiểu" />
                                    </div>
                                )}

                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Ghi chú</label>
                                    <div style={{ fontSize: 14, color: '#0098F6', fontWeight: 600, padding: '10px 0' }}>
                                        {form.type === 'redeem'
                                            ? `${form.pointsRequired || '?'} điểm = ${formatVND(form.rewardValue || 0)} VNĐ`
                                            : form.earnMode === 'by_bill'
                                                ? `Mỗi hoá đơn = ${form.rewardValue || '?'} điểm`
                                                : `Chi ${formatVND(form.pointsRequired || 0)} VNĐ = ${form.rewardValue || '?'} điểm`}
                                    </div>
                                </div>

                                {panelMode === 'edit' && (
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Trạng thái</label>
                                        <div className={styles.typeToggle}>
                                            <button className={`${styles.typeBtn}${form.isActive ? ` ${styles.typeBtnActive}` : ''}`}
                                                onClick={() => setForm((p) => ({ ...p, isActive: true }))}>Đang bật</button>
                                            <button className={`${styles.typeBtn}${!form.isActive ? ` ${styles.typeBtnActive}` : ''}`}
                                                onClick={() => setForm((p) => ({ ...p, isActive: false }))}>Tắt</button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className={styles.panelFooter}>
                                {panelMode === 'edit' && (
                                    <button className={`${styles.panelBtn} ${styles.panelBtnDanger}`} onClick={handleDelete}>Xoá</button>
                                )}
                                <div style={{ flex: 1 }} />
                                <button className={styles.panelBtn} onClick={closePanel}>Đóng</button>
                                <button className={`${styles.panelBtn} ${styles.panelBtnPrimary}`} onClick={handleSave} disabled={saving}>
                                    {saving ? 'Đang lưu...' : panelMode === 'create' ? 'Tạo' : 'Lưu'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {toastMsg && (
                <div className={styles.toastOverlay}>
                    <div className={styles.toastModal}>
                        <div className={styles.toastIcon} style={toastMsg.type === 'error' ? { background: '#EF4444' } : undefined}>
                            {toastMsg.type === 'success' ? '✓' : '✕'}
                        </div>
                        <span className={styles.toastText}>{toastMsg.text}</span>
                    </div>
                </div>
            )}
        </div>
    );
}
